import { inject,Injectable } from "@angular/core";

import html2canvas from "html2canvas";
import jspdf from "jspdf";

import { LoggerService } from "@core/services/logger.service";

import { MarkdownService } from "./markdown.service";

@Injectable({
  providedIn: "root"
})
export class PdfExportService {
  private readonly _markdownService = inject(MarkdownService);
  private readonly _logger = inject(LoggerService);

  private readonly _pdfMarginPt = 40;
  private readonly _pageBreakSearchPx = 120;

  /**
   * Export document as PDF format
   */
  async exportToPdf(title: string, markdown: string): Promise<Blob> {
    try {
      const html = this._markdownService.markdownToHtml(markdown);

      // Offscreen container that inherits app styles (so output matches the app)
      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-10000px";
      container.style.top = "0";
      container.style.width = "900px"; // Wider source for better text quality
      container.style.padding = "24px";
      container.style.background = "#ffffff";
      container.innerHTML = this._createHtmlMarkup(title, html);
      document.body.appendChild(container);

      try {
        // Render to a single tall canvas, then slice into PDF pages
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          allowTaint: false,
          logging: false
        });

        const doc = new jspdf("p", "pt", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = this._pdfMarginPt;
        const imgWidth = pageWidth - margin * 2;

        // How many source pixels fit into one PDF page (accounting for margins and scaling)
        const pageContentHeightPt = pageHeight - margin * 2;
        const pxPerPt = canvas.width / imgWidth; // pixels corresponding to 1pt of width
        const pageHeightPx = Math.floor(pageContentHeightPt * pxPerPt);

        const sourceCtx = canvas.getContext("2d", { willReadFrequently: true });
        if (!sourceCtx) throw new Error("2D context not available");

        let offsetY = 0;
        let firstPage = true;
        while (offsetY < canvas.height) {
          const remaining = canvas.height - offsetY;
          const sliceHeightPx =
            remaining <= pageHeightPx
              ? remaining
              : this._findBestPageBreakY({
                  ctx: sourceCtx,
                  offsetY,
                  idealSliceHeightPx: pageHeightPx,
                  canvasWidth: canvas.width,
                  canvasHeight: canvas.height
                }) - offsetY;

          // Create a canvas slice for current page
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHeightPx;
          const ctx = sliceCanvas.getContext("2d");
          if (!ctx) throw new Error("2D context not available");
          ctx.drawImage(canvas, 0, offsetY, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

          const sliceHeightPt = sliceHeightPx / pxPerPt;
          const dataUrl = sliceCanvas.toDataURL("image/jpeg", 0.92);

          if (!firstPage) {
            doc.addPage();
          }
          doc.addImage(dataUrl, "JPEG", margin, margin, imgWidth, sliceHeightPt);

          firstPage = false;
          offsetY += sliceHeightPx;
        }

        return doc.output("blob");
      } finally {
        document.body.removeChild(container);
      }
    } catch (error) {
      this._logger.error("PDF export failed", error as any);
      throw new Error("Failed to generate PDF document");
    }
  }

  /**
   * Export document as HTML (for PDF generation)
   */
  exportToHtml(markdown: string, title: string = "Document"): string {
    const html = this._markdownService.markdownToHtml(markdown);
    return this._createHtmlMarkup(title, html);
  }

  private _createHtmlMarkup(title: string, html: string): string {
    return `
<div class="markdown-content">
  <h1>${title}</h1>
  ${html}
  
  <style>
    /* Force LIGHT THEME variables locally so dark mode doesn't leak in */
    .markdown-content {
      background-color: #ffffff;
      /* Text */
      --md-text-primary: rgb(15, 23, 42);
      --md-text-secondary: rgb(100, 116, 139);
      --md-text-tertiary: rgb(100, 116, 139);
      --md-text-body: rgb(15, 23, 42);
      --md-text-strong: rgb(15, 23, 42);
      --md-text-emphasis: rgb(156, 96, 255);
      --md-text-muted: rgb(148, 163, 184);
      --md-text-code: rgb(139, 92, 246);

      /* Backgrounds */
      --md-bg-subtle: rgb(248, 250, 252);
      --md-bg-surface: rgb(250, 251, 252);
      --md-bg-code: rgb(241, 245, 249);
      --md-bg-code-block: rgb(248, 250, 252);
      --md-bg-highlight: rgba(254, 240, 138, 0.5);
      --md-bg-zebra: rgb(248, 250, 252);
      --md-bg-hover: rgb(241, 245, 249);
      --md-bg-hover-section: rgba(156, 96, 255, 0.04);
      --md-bg-editing: rgba(34, 197, 94, 0.04);
      --md-bg-kbd: rgb(226, 232, 240);
      --md-bg-blockquote: rgb(248, 250, 252);

      /* Borders */
      --md-border-light: rgb(203, 213, 225);
      --md-border-accent: rgb(156, 96, 255);
      --md-border-editing: rgb(34, 197, 94);
      --md-border-button: rgb(203, 213, 225);
      --md-border-kbd: rgb(203, 213, 225);
      --md-border-blockquote: rgb(59, 130, 246);

      /* Buttons */
      --md-bg-button: rgb(255, 255, 255);
      --md-bg-button-hover: rgb(241, 245, 249);
      --md-text-button: rgb(51, 65, 85);
      --md-btn-primary-bg: rgb(156, 96, 255);
      --md-btn-primary-text: white;
      --md-btn-primary-border: rgb(156, 96, 255);
      --md-btn-secondary-bg: rgb(248, 250, 252);
      --md-btn-secondary-text: rgb(51, 65, 85);
      --md-btn-secondary-border: rgb(203, 213, 225);

      /* Links */
      --md-link-color: rgb(156, 96, 255);
      --md-link-hover: rgb(37, 99, 235);
      --md-link-visited: rgb(59, 130, 246);

      /* Shadows */
      --md-shadow-kbd: rgba(0, 0, 0, 0.1);
      --md-shadow-section: rgba(0, 0, 0, 0.05);
    }

    /* Avoid cutting code blocks and tables between slices where possible */
    pre, table, blockquote, img { break-inside: avoid; page-break-inside: avoid; }
    h1, h2, h3 { break-after: avoid; }
    p, li { break-inside: avoid; page-break-inside: avoid; }
  </style>
</div>`;
  }

  private _findBestPageBreakY(params: {
    ctx: CanvasRenderingContext2D;
    offsetY: number;
    idealSliceHeightPx: number;
    canvasWidth: number;
    canvasHeight: number;
  }): number {
    const { ctx, offsetY, idealSliceHeightPx, canvasWidth, canvasHeight } = params;

    const idealBreakY = offsetY + idealSliceHeightPx;
    const minY = Math.min(canvasHeight - 1, Math.max(offsetY + Math.floor(idealSliceHeightPx * 0.6), 1));
    const maxY = Math.min(canvasHeight - 1, Math.max(idealBreakY + 1, 1));

    const searchFrom = Math.max(minY, idealBreakY - this._pageBreakSearchPx);
    const searchTo = Math.min(maxY, idealBreakY + this._pageBreakSearchPx);

    const xStart = Math.floor(canvasWidth * 0.06);
    const sampleWidth = Math.max(1, Math.floor(canvasWidth * 0.88));
    const sampleHeight = 3;
    const whiteThreshold = 248;

    let bestY = idealBreakY;
    let bestScore = Number.POSITIVE_INFINITY;

    for (let y = searchFrom; y <= searchTo; y += 1) {
      const h = Math.min(sampleHeight, canvasHeight - y);
      if (h <= 0) break;

      const data = ctx.getImageData(xStart, y, sampleWidth, h).data;
      let darkCount = 0;
      const pixelCount = data.length / 4;

      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a === 0) continue;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        if (r < whiteThreshold || g < whiteThreshold || b < whiteThreshold) darkCount += 1;
      }

      const score = darkCount / Math.max(1, pixelCount);

      if (score < bestScore || (score === bestScore && Math.abs(y - idealBreakY) < Math.abs(bestY - idealBreakY))) {
        bestScore = score;
        bestY = y;
        if (bestScore < 0.002) break;
      }
    }

    const safeMinAdvance = Math.floor(idealSliceHeightPx * 0.75);
    if (bestY <= offsetY + safeMinAdvance) return offsetY + idealSliceHeightPx;
    return bestY;
  }
}
